import * as React from 'react'
import { getErrorDescription } from '@/lib/error-utils'
import { useForm } from '@tanstack/react-form'
import { create } from '@bufbuild/protobuf'
import { FieldMaskSchema } from '@bufbuild/protobuf/wkt'
import { toast } from 'sonner'
import { captureError } from '@/lib/sentry'
import { reportError } from '@/lib/error-reporting'
import { Minus, Plus } from 'lucide-react'
import { useNavigate } from '@tanstack/react-router'
import { useStore } from '@tanstack/react-store'

import type { Inquiry } from '@/gen/cortado/v1/inquiries_pb'
import { UpdateInquiryRequestSchema, CreateInquiryRequestSchema, CreateContactRequestSchema } from '@/gen/cortado/v1/cortado_pb'
import { InquiryStageEnum } from '@/gen/cortado/v1/settings_pb'
import { STAGE_OPTIONS } from '@/components/leads/stage-selector-dropdown'
import { useUpdateInquiry, useCreateInquiry, useUpdateInquiryEvent } from '@/stores/leads.store'
import { useCreateContact } from '@/stores/contacts.store'
import { useListItems } from '@/stores/organization.store'
import { organizationStore } from '@/stores/organization.store'
import {
  calculateServiceTimeRange,
  formatTimestampToTimeString,
  getFirstEventDetails,
  MINUTE_IN_HOURS,
} from '@/lib/inquiry-utils'
import { dateToTimestamp } from '@/lib/date-utils'
import { useEventTypes } from '@/hooks/use-event-types'
import { cn, formatPhoneInput, stripPhoneFormatting } from '@/lib/utils'

import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetClose,
} from '@/components/ui/sheet'
import type { SheetLayout } from '@/components/shared/sheet-layout'
import { Input } from '@/components/ui/input'
import { InputGroupInput } from '@/components/ui/input-group'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { PlacesAutocomplete } from '@/components/ui/places-autocomplete'
import { InputGroup } from '@/components/ui/input-group'
import { Checkbox } from '@/components/ui/checkbox'
import { DurationInput } from '@/components/shared/duration-input'
import { DatePicker } from '@/components/shared/date-picker'
import { TimePicker } from '@/components/shared/time-picker'
import { ContactSelector } from '@/components/contacts/contact-selector'
import { DuplicateContactAlert } from '@/components/contacts/duplicate-contact-alert'
import { useCheckDuplicateContactEmail } from '@/hooks/use-check-duplicate-contact-email'

interface InquirySheetProps {
  mode: 'create' | 'update'
  inquiry?: Inquiry
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Default stage to pre-select when creating a new inquiry (only used in create mode) */
  defaultStage?: InquiryStageEnum
  /** Pre-selected contact ID — skips the contact selector when provided in create mode */
  defaultContactId?: string
  /**
   * Called with the new lead in create mode. When set, the caller decides where
   * to go next, so the success toast carries no "View Lead" action.
   */
  onCreated?: (inquiry: Inquiry) => void
  /** 'pipeline' applies the shared pipeline sheet width and footer layout. */
  layout?: SheetLayout
}

function isEventInPast(eventDate?: Date, eventTime?: string): boolean {
  if (!eventDate) return false
  const candidate = new Date(eventDate)
  if (eventTime) {
    const [hours, minutes] = eventTime.split(':').map(Number)
    candidate.setHours(hours, minutes, 0, 0)
  } else {
    candidate.setHours(0, 0, 0, 0)
  }
  return candidate < new Date()
}

/**
 * InquirySheet - Reusable sheet component for creating and updating inquiry details
 *
 * Features:
 * - Create new inquiries with contact selection/creation
 * - Update existing inquiry event type, date/time, duration, guest count, and location
 * - Uses TanStack Form for form state management
 * - Leverages useCreateInquiry/useUpdateInquiry hooks for optimized cache updates
 * - Displays loading states during mutation
 * - Shows toast notifications with action button on success
 * - Resets form on close
 *
 * @param mode - 'create' or 'update' mode
 * @param inquiry - The inquiry to edit (required for update mode)
 * @param open - Control sheet visibility
 * @param onOpenChange - Handle open/close state changes
 */
export function InquirySheet({
  mode,
  inquiry,
  open,
  onOpenChange,
  defaultStage,
  defaultContactId,
  onCreated,
  layout,
}: InquirySheetProps) {
  const { mutation: updateMutation } = useUpdateInquiry()
  const { mutation: createMutation } = useCreateInquiry()
  const { updateEvent, mutation: updateInquiryEventMutation } = useUpdateInquiryEvent(inquiry?.id ?? '')
  const { mutation: createContactMutation } = useCreateContact()
  const navigate = useNavigate()
  const selectedOrganizationId = useStore(organizationStore, (state) => state.selectedOrganizationId)
  const [isCreatingNewContact, setIsCreatingNewContact] = React.useState(false)
  const hasDefaultContact = mode === 'create' && !!defaultContactId

  // Get the mutation based on mode
  const mutation = mode === 'create' ? createMutation : updateMutation

  // Fetch items at the top level (not inside render functions)
  const orgId = mode === 'create' ? selectedOrganizationId : inquiry?.organizationId
  const { items, isLoading: itemsLoading } = useListItems(orgId || '')
  const formItems = items?.filter(item => item.includeOnForm) ?? []

  // Get first event details for the form (for backwards compatibility)
  const firstEvent = getFirstEventDetails(inquiry?.events)

  // Archived types are not assignable to new records, but the one already
  // on this record stays selectable so the field isn't blank.
  const { getEventTypeOptions } = useEventTypes()

  // Setup TanStack Form
  const form = useForm({
    defaultValues: {
      contactId: mode === 'create' ? (defaultContactId ?? '') : (inquiry?.contact?.id ?? ''),
      stage: mode === 'create' ? (defaultStage ?? InquiryStageEnum.INQUIRY_STAGE_NEW) : (inquiry?.stage ?? InquiryStageEnum.INQUIRY_STAGE_NEW),
      eventType: mode === 'create' ? '' : (inquiry?.eventTypeKey ?? ''),
      eventDate: mode === 'create' ? undefined : firstEvent.eventDate,
      eventTime: mode === 'create' ? '' : formatTimestampToTimeString(inquiry?.events[0]?.eventDate),
      eventLength: mode === 'create' ? 0.5 : firstEvent.eventLength,
      eventGuests: mode === 'create' ? 0 : firstEvent.eventGuests,
      eventLocation: mode === 'create' ? '' : (inquiry?.eventLocation ?? ''),
      itemIds: mode === 'create' ? [] : (inquiry?.itemIds ?? []),
      // Contact fields for inline creation
      contactFirstName: '',
      contactLastName: '',
      contactEmail: '',
      contactPhoneNumber: '',
    },
    onSubmit: async ({ value }) => {
      try {
        // Combine date and time into a Date object
        let eventDate: Date | undefined
        if (value.eventDate && value.eventTime) {
          const timeParts = value.eventTime.split(':')
          const hours = parseInt(timeParts[0], 10)
          const minutes = parseInt(timeParts[1], 10)
          eventDate = new Date(value.eventDate)
          eventDate.setHours(hours, minutes, 0, 0)
        } else if (value.eventDate) {
          eventDate = new Date(value.eventDate)
          eventDate.setHours(0, 0, 0, 0)
        }

        // Prevent past dates when creating a new lead
        if (mode === 'create' && isEventInPast(value.eventDate, value.eventTime)) {
          toast.error('Event date and time cannot be in the past')
          return
        }

        if (mode === 'create') {
          if (!selectedOrganizationId) {
            toast.error('No organization selected')
            return
          }

          let contactId = value.contactId

          // If creating new contact, do that first
          if (isCreatingNewContact) {
            const firstName = value.contactFirstName.trim()
            const lastName = value.contactLastName.trim()
            const email = value.contactEmail.trim()
            const phoneDigits = stripPhoneFormatting(value.contactPhoneNumber)

            if (!firstName && !lastName && !email && !phoneDigits) {
              toast.error('Please enter at least one contact detail')
              return
            }

            const contactRequest = create(CreateContactRequestSchema, {
              organizationId: selectedOrganizationId,
              firstName,
              lastName,
              email: email || undefined,
              phoneNumber: phoneDigits || undefined,
            })

            const contactResponse = await createContactMutation.mutateAsync(contactRequest)

            if (!contactResponse.contact) {
              toast.error('Failed to create contact')
              return
            }

            contactId = contactResponse.contact.id
          } else {
            // Validate that a contact is selected
            if (!contactId) {
              toast.error('Please select or create a contact')
              return
            }
          }

          // Build CreateInquiryRequest
          const request = create(CreateInquiryRequestSchema, {
            organizationId: selectedOrganizationId,
            contactId,
            eventTypeKey: value.eventType || undefined,
            eventDate: eventDate ? dateToTimestamp(eventDate) : undefined,
            eventLength: value.eventLength > 0 ? value.eventLength : undefined,
            eventGuests: value.eventGuests >= 0 ? value.eventGuests : undefined,
            eventLocation: value.eventLocation.trim() || undefined,
            stage: value.stage !== InquiryStageEnum.INQUIRY_STAGE_UNKNOWN ? value.stage : undefined,
          })

          const response = await createMutation.mutateAsync(request)

          if (response.inquiry && onCreated) {
            toast.success('Lead created')
            onCreated(response.inquiry)
          } else if (response.inquiry) {
            const newInquiryId = response.inquiry.id

            toast.success('Lead created', {
              action: {
                label: 'View Lead',
                onClick: () => navigate({
                  to: '/leads/$id',
                  params: { id: newInquiryId }
                })
              }
            })
          }
        } else {
          // Build UpdateInquiryRequest
          if (!inquiry) {
            toast.error('No inquiry to update')
            return
          }
          const firstInquiryEvent = inquiry.events[0]
          if (!firstInquiryEvent) {
            toast.error('This lead has no event to update')
            return
          }

          const request = create(UpdateInquiryRequestSchema, {
            id: inquiry.id,
            eventTypeKey: value.eventType || undefined,
            eventLocation: value.eventLocation.trim() || undefined,
            itemIds: value.itemIds,
            updateMask: create(FieldMaskSchema, {
				paths: ['event_type_key', 'event_location', 'item_ids'],
            }),
          })

          await updateEvent({
            id: firstInquiryEvent.id,
            eventDate: eventDate ? dateToTimestamp(eventDate) : undefined,
            eventLength: value.eventLength > 0 ? value.eventLength : undefined,
            eventGuests: value.eventGuests >= 0 ? value.eventGuests : undefined,
            updateMask: ['event_date', 'event_length', 'event_guests'],
          })
          await updateMutation.mutateAsync(request)

          toast.success('Lead updated successfully')
        }

        onOpenChange(false)
        form.reset()
      } catch (error) {
        console.error(`Failed to ${mode} inquiry:`, error)
        reportError(error, { feature: `inquiry_${mode}`, kind: 'mutation' })
        captureError(error instanceof Error ? error : new Error(String(error)), { context: 'inquiry-sheet' })
        toast.error(`Failed to ${mode} lead`, {
          description: getErrorDescription(error),
        })
      }
    },
  })

  // Reset isCreatingNewContact when sheet closes
  React.useEffect(() => {
    if (!open) {
      setIsCreatingNewContact(false)
    }
  }, [open])


  // Combined pending state
  const isPending = mutation.isPending || updateInquiryEventMutation.isPending || createContactMutation.isPending

  // Check for duplicate email when creating new contact inline
  const { existingContact, checkEmail, clearExistingContact } = useCheckDuplicateContactEmail({
    organizationId: selectedOrganizationId,
    disabled: !isCreatingNewContact || mode !== 'create',
  })

  // Handle using an existing contact instead of creating new
  const handleUseExistingContact = (contactId: string) => {
    form.setFieldValue('contactId', contactId)
    // Clear the inline contact fields
    form.setFieldValue('contactFirstName', '')
    form.setFieldValue('contactLastName', '')
    form.setFieldValue('contactEmail', '')
    form.setFieldValue('contactPhoneNumber', '')
    setIsCreatingNewContact(false)
    clearExistingContact()
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className={cn(layout === 'pipeline' && 'w-full sm:max-w-xl')}>
        <SheetHeader>
          <SheetTitle>{mode === 'create' ? 'Create New Lead' : 'Edit Lead Details'}</SheetTitle>
          <SheetDescription>
            {mode === 'create'
              ? 'Create a new lead by selecting a contact and providing event details.'
              : 'Make changes to the lead information. Click save when you\'re done.'}
          </SheetDescription>
        </SheetHeader>
        <SheetBody>
          <form
            id="inquiry-form"
            onSubmit={(e) => {
              e.preventDefault()
              e.stopPropagation()
              form.handleSubmit()
            }}
            className="grid auto-rows-min gap-6"
          >
          {/* Contact selector - only show in create mode when no default contact */}
          {mode === 'create' && !hasDefaultContact && (
            <>
              {!isCreatingNewContact ? (
                <form.Field name="contactId">
                  {(field) => (
                    <ContactSelector
                      value={field.state.value}
                      onChange={(contactId) => field.handleChange(contactId)}
                      disabled={isPending}
                      onNewContactClick={() => setIsCreatingNewContact(true)}
                    />
                  )}
                </form.Field>
              ) : (
                <div className="space-y-4 rounded-lg border p-4">
                  <div className="flex items-center justify-between">
                    <Label>New Contact</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsCreatingNewContact(false)}
                      disabled={isPending}
                    >
                      Select Existing
                    </Button>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <form.Field name="contactFirstName">
                      {(field) => (
                        <div className="grid gap-2">
                          <Label htmlFor="contactFirstName">First Name</Label>
                          <Input
                            id="contactFirstName"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            onBlur={field.handleBlur}
                            disabled={isPending}
                            placeholder="John"
                          />
                        </div>
                      )}
                    </form.Field>

                    <form.Field name="contactLastName">
                      {(field) => (
                        <div className="grid gap-2">
                          <Label htmlFor="contactLastName">Last Name</Label>
                          <Input
                            id="contactLastName"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            onBlur={field.handleBlur}
                            disabled={isPending}
                            placeholder="Doe"
                          />
                        </div>
                      )}
                    </form.Field>
                  </div>

                  <form.Field name="contactEmail">
                    {(field) => (
                      <div className="grid gap-2">
                        <Label htmlFor="contactEmail">Email</Label>
                        <Input
                          id="contactEmail"
                          type="email"
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          onBlur={() => {
                            field.handleBlur()
                            checkEmail(field.state.value)
                          }}
                          disabled={isPending}
                          placeholder="john@example.com"
                        />
                      </div>
                    )}
                  </form.Field>

                  {existingContact && (
                    <DuplicateContactAlert
                      contact={existingContact}
                      onUseExisting={handleUseExistingContact}
                      disabled={isPending}
                    />
                  )}

                  <form.Field name="contactPhoneNumber">
                    {(field) => (
                      <div className="grid gap-2">
                        <Label htmlFor="contactPhoneNumber">Phone</Label>
                        <Input
                          id="contactPhoneNumber"
                          type="tel"
                          inputMode="numeric"
                          value={field.state.value}
                          onChange={(e) => field.handleChange(formatPhoneInput(e.target.value))}
                          onBlur={field.handleBlur}
                          disabled={isPending}
                          placeholder="(555) 123-4567"
                        />
                      </div>
                    )}
                  </form.Field>
                </div>
              )}
            </>
          )}

          {/* Stage selector - only show in create mode */}
          {mode === 'create' && (
            <form.Field name="stage">
              {(field) => (
                <div className="grid gap-3">
                  <Label htmlFor="stage">Stage</Label>
                  <Select
                    value={String(field.state.value)}
                    onValueChange={(value) => field.handleChange(Number(value))}
                    disabled={isPending}
                  >
                    <SelectTrigger id="stage" className="w-full">
                      <SelectValue placeholder="Select stage" />
                    </SelectTrigger>
                    <SelectContent>
                      {STAGE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={String(option.value)}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>
          )}

          <form.Field name="eventType">
            {(field) => (
              <div className="grid gap-3">
                <Label htmlFor="event-type">Event Type</Label>
                <Select
                  value={field.state.value}
                  onValueChange={(value) => field.handleChange(value)}
                  disabled={isPending}
                >
                  <SelectTrigger id="event-type" className="w-full">
                    <SelectValue placeholder="Select event type" />
                  </SelectTrigger>
                  <SelectContent>
                    {getEventTypeOptions(field.state.value).map((eventType) => (
                      <SelectItem key={eventType.key} value={eventType.key}>
                        {eventType.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </form.Field>

          <div className="grid grid-cols-2 gap-4">
            <form.Field name="eventDate">
              {(field) => (
                <div className="grid gap-3">
                  <Label htmlFor="event-date">Event Date</Label>
                  <DatePicker
                    value={field.state.value}
                    onChange={(date) => field.handleChange(date)}
                    disabled={isPending}
                    minDate={mode === 'create' ? new Date() : undefined}
                  />
                </div>
              )}
            </form.Field>

            <form.Field name="eventTime">
              {(field) => (
                <div className="grid gap-3">
                  <Label htmlFor="event-time">Event Time</Label>
                  <TimePicker
                    value={field.state.value}
                    onChange={(time) => field.handleChange(time)}
                    disabled={isPending}
                    placeholder="Select time"
                  />
                </div>
              )}
            </form.Field>
          </div>

          {mode === 'create' && (
            <form.Subscribe
              selector={(s) => [s.values.eventDate, s.values.eventTime] as const}
            >
              {([eventDate, eventTime]) =>
                isEventInPast(eventDate, eventTime) ? (
                  <p className="-mt-3 text-sm text-destructive">
                    Event date and time cannot be in the past.
                  </p>
                ) : null
              }
            </form.Subscribe>
          )}

          <form.Field name="eventLength">
            {(field) => {
              const serviceTime = calculateServiceTimeRange(
                form.state.values.eventTime,
                field.state.value
              )

              return (
                <div className="grid gap-3">
                  <Label htmlFor="event-length">Duration</Label>
                  <div className="flex items-center rounded-md border border-input shadow-xs overflow-hidden">
                    <DurationInput
                      value={field.state.value}
                      onChange={(value) => field.handleChange(value)}
                      disabled={isPending}
                      min={MINUTE_IN_HOURS}
                      step={0.25}
                    />
                  </div>
                  {serviceTime && (
                    <p className="text-sm text-muted-foreground px-1">
                      {serviceTime}
                    </p>
                  )}
                </div>
              )
            }}
          </form.Field>

          <form.Field name="eventGuests">
            {(field) => (
              <div className="grid gap-3">
                <Label htmlFor="event-guests">Guest Count</Label>
                <div className="flex items-center rounded-md border border-input shadow-xs overflow-hidden">
                  <div className="flex border-r border-input">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        const newValue = Math.max(0, field.state.value - 25)
                        field.handleChange(newValue)
                      }}
                      disabled={isPending || field.state.value <= 0}
                    >
                      <Minus className="h-4 w-4" />
                      <span className="sr-only">Decrease by 25</span>
                    </Button>
                  </div>
                  <InputGroupInput
                    id="event-guests"
                    type="number"
                    min="0"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(Number(e.target.value) || 0)}
                    onBlur={(e) => {
                      const value = Number(e.target.value) || 0
                      // Enforce minimum of 0
                      const clampedValue = Math.max(0, value)
                      field.handleChange(clampedValue)
                      field.handleBlur()
                    }}
                    disabled={isPending}
                    placeholder="0"
                    className="text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <div className="flex border-l border-input">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        const newValue = field.state.value + 25
                        field.handleChange(newValue)
                      }}
                      disabled={isPending}
                    >
                      <Plus className="h-4 w-4" />
                      <span className="sr-only">Increase by 25</span>
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </form.Field>

          <form.Field name="eventLocation">
            {(field) => (
              <div className="grid gap-3">
                <Label htmlFor="event-location">Location</Label>
                <InputGroup>
                  <PlacesAutocomplete
                    id="event-location"
                    value={field.state.value}
                    onChange={(value) => field.handleChange(value)}
                    onBlur={field.handleBlur}
                    placeholder="Enter event location"
                    disabled={isPending}
                  />
                </InputGroup>
              </div>
            )}
          </form.Field>

          {/* Only show items selector in update mode */}
          {mode === 'update' && (
            <form.Field name="itemIds">
              {(field) => {
                return (
                  <div className="grid gap-3">
                    <Label>Services & Items</Label>
                    {itemsLoading ? (
                      <p className="text-sm text-muted-foreground">Loading items...</p>
                    ) : formItems.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No items available</p>
                    ) : (
                      <div className="space-y-2 max-h-[200px] overflow-y-auto border rounded-md p-3">
                        {formItems.map((item) => (
                          <div key={item.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`item-${item.id}`}
                              checked={field.state.value.includes(item.id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  field.handleChange([...field.state.value, item.id])
                                } else {
                                  field.handleChange(field.state.value.filter(id => id !== item.id))
                                }
                              }}
                              disabled={isPending}
                            />
                            <Label htmlFor={`item-${item.id}`} className="cursor-pointer">
                              <span className="font-normal">{item.title}</span>
                            </Label>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )
              }}
            </form.Field>
          )}

          </form>
        </SheetBody>
        <SheetFooter className={cn(layout === 'pipeline' && 'sm:justify-between')}>
          <SheetClose asChild>
            <Button type="button" variant="outline" disabled={isPending}>
              Cancel
            </Button>
          </SheetClose>
          <form.Subscribe
            selector={(s) => [
              s.values.contactId,
              s.values.contactFirstName,
              s.values.contactLastName,
              s.values.contactEmail,
              s.values.contactPhoneNumber,
              s.values.eventDate,
              s.values.eventTime,
              s.isSubmitting,
            ] as const}
          >
            {([contactId, contactFirstName, contactLastName, contactEmail, contactPhoneNumber, eventDate, eventTime, isSubmitting]) => {
              let disabled = isPending || isSubmitting
              if (mode === 'create') {
                if (!hasDefaultContact) {
                  const contactReady = isCreatingNewContact
                    ? !!(
                        contactFirstName.trim() ||
                        contactLastName.trim() ||
                        contactEmail.trim() ||
                        stripPhoneFormatting(contactPhoneNumber)
                      )
                    : !!contactId
                  disabled = disabled || !contactReady
                }
                if (isEventInPast(eventDate, eventTime)) {
                  disabled = true
                }
              }
              return (
                <Button type="submit" form="inquiry-form" disabled={disabled}>
                  {isPending ? (mode === 'create' ? 'Creating...' : 'Saving...') : (mode === 'create' ? 'Create Lead' : 'Save Changes')}
                </Button>
              )
            }}
          </form.Subscribe>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}

// Export old name for backward compatibility
export { InquirySheet as InquiryUpdateSheet }
